import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import { tracks } from '../data/mockData';

const Waveform = ({ active }) => (
  <div className="flex items-end gap-[2px] h-6 w-24 mx-4">
    {[...Array(12)].map((_, i) => (
      <div 
        key={i} 
        className={`w-1.5 bg-gradient-to-t from-plasma to-aqua rounded-t-sm transition-all duration-300 ${active ? 'animate-wave' : 'h-1'}`}
        style={{ 
          animationDelay: `${i * 0.1}s`,
          height: active ? undefined : '4px',
          opacity: active ? 1 : 0.3
        }} 
      />
    ))}
  </div>
);

export default function Player() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [trackIndex, setTrackIndex] = useState(0);
  
  const audioRef = useRef(null);
  const currentTrack = tracks[trackIndex];

  // Sync play/pause state with the actual audio element
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(err => {
          console.error("Audio playback failed:", err);
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, trackIndex]);

  // Sync volume state with the actual audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlay = () => setIsPlaying(!isPlaying);

  const nextTrack = () => {
    setTrackIndex((prev) => (prev + 1) % tracks.length);
    setIsPlaying(true);
  };

  const prevTrack = () => {
    setTrackIndex((prev) => (prev === 0 ? tracks.length - 1 : prev - 1));
    setIsPlaying(true);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === Infinity) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-24 bg-vinyl-800/95 backdrop-blur-xl border-t border-vinyl-700/80 flex items-center justify-between px-4 md:px-8 relative z-50">
      
      {/* Hidden Native Audio Element */}
      <audio
        ref={audioRef}
        src={currentTrack.audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={nextTrack}
        preload="metadata"
      />

      {/* Top Progress Bar Line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-vinyl-700">
        <div 
          className="h-full bg-gradient-to-r from-plasma to-aqua shadow-[0_0_10px_#00FFFF] transition-all duration-100" 
          style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
        />
      </div>

      {/* Track Info */}
      <div className="flex items-center gap-4 w-1/3 min-w-[200px]">
        <img 
          src={currentTrack.cover} 
          alt="Cover" 
          className={`w-14 h-14 rounded-md object-cover transition-transform duration-500 ${isPlaying ? 'scale-105 shadow-lg' : 'scale-100'}`} 
        />
        <div className="hidden sm:block">
          <h4 className="text-white font-medium text-sm truncate">{currentTrack.title}</h4>
          <p className="text-gray-400 text-xs truncate">{currentTrack.artist}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col items-center justify-center flex-1 max-w-md">
        <div className="flex items-center gap-6">
          <button onClick={prevTrack} className="text-gray-200 hover:text-aqua transition-colors">
            <SafeIcon icon="FiSkipBack" />
          </button>
          
          <button 
            onClick={togglePlay} 
            className="w-12 h-12 rounded-full bg-white text-vinyl-900 flex items-center justify-center hover:scale-105 transition-transform shadow-[0_0_15px_rgba(255,255,255,0.3)]"
          >
            <SafeIcon icon={isPlaying ? "FiPause" : "FiPlay"} className="text-xl ml-1" />
          </button>
          
          <button onClick={nextTrack} className="text-gray-200 hover:text-aqua transition-colors">
            <SafeIcon icon="FiSkipForward" />
          </button>
        </div>
        
        <div className="w-full flex items-center gap-2 mt-2 hidden sm:flex">
          <span className="text-[10px] text-gray-400 w-8 text-right font-mono">
            {formatTime(currentTime)}
          </span>
          <input 
            type="range" 
            min="0" 
            max={duration || 0} 
            step="0.1"
            value={currentTime} 
            onChange={handleProgressChange}
            className="h-1 flex-1 bg-vinyl-700 rounded-full cursor-pointer accent-aqua" 
          />
          <span className="text-[10px] text-gray-400 w-8 font-mono">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* Right Controls */}
      <div className="w-1/3 flex items-center justify-end gap-4">
        <Waveform active={isPlaying} />
        <div className="hidden lg:flex items-center gap-2 w-24">
          <SafeIcon icon={volume === 0 ? "FiVolumeX" : "FiVolume2"} className="text-gray-400 text-sm" />
          <input 
            type="range" 
            min="0" 
            max="1" 
            step="0.01"
            value={volume} 
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="h-1 flex-1 bg-vinyl-700 rounded-full cursor-pointer accent-plasma" 
          />
        </div>
      </div>
    </div>
  );
}